import {
  InterceptToggleInputSchema,
  INTERCEPT_TOGGLE_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import { getInterceptSettingsForAction } from '../../../../utils/interceptMode.utils';
import { useProxyStore } from '../../../../store/proxy-store';
import {
  getElectronApiUnavailableResult,
  isElectronApiAvailable,
  parsePortalToolHandlerInput,
} from '../portal-tool-handler.utils';
import type { PortalToolHandler } from '../types';

type InterceptQueueProcessAction = 'forward' | 'drop';

async function processInterceptQueueItem({
  item,
  action,
  proxyStore,
}: {
  item: ReturnType<typeof useProxyStore.getState>['interceptQueue'][number];
  action: InterceptQueueProcessAction;
  proxyStore: ReturnType<typeof useProxyStore.getState>;
}): Promise<{ success: boolean; error?: string }> {
  if (item.type === 'response') {
    const responseResult =
      action === 'forward'
        ? await window.electronAPI.intercept.resumeResponse(item.id)
        : await window.electronAPI.intercept.dropResponse(item.id);

    if (responseResult.success) {
      proxyStore.removeFromInterceptQueue(item.id);
      return { success: true };
    }

    return {
      success: false,
      error: responseResult.error ?? 'Unknown error',
    };
  }

  const requestResult =
    action === 'forward'
      ? await window.electronAPI.intercept.resumeRequest(item.id)
      : await window.electronAPI.intercept.dropRequest(item.id);

  if (requestResult.success) {
    if (!proxyStore.settings.interceptResponsesEnabled) {
      proxyStore.removeFromInterceptQueue(item.id);
    } else {
      proxyStore.updateInterceptedRequestStatus(item.id, 'resumed');
    }
    return { success: true };
  }

  return {
    success: false,
    error: requestResult.error ?? 'Unknown error',
  };
}

export const interceptToggleHandler: PortalToolHandler = async ({
  input,
  toolCallId,
  batchId,
}) => {
  const parsedInput = parsePortalToolHandlerInput({
    schema: InterceptToggleInputSchema,
    input,
    toolName: INTERCEPT_TOGGLE_TOOL_NAME,
    toolCallId,
    batchId,
  });
  if (!parsedInput.ok) {
    return parsedInput.result;
  }

  if (!isElectronApiAvailable()) {
    return getElectronApiUnavailableResult({
      toolName: INTERCEPT_TOGGLE_TOOL_NAME,
      toolCallId,
      batchId,
    });
  }

  const proxyStore = useProxyStore.getState();

  switch (parsedInput.data.action) {
    case 'list-queue': {
      const currentSettings = useProxyStore.getState().settings;
      const queue = useProxyStore.getState().interceptQueue.map((item) => ({
        queueItemId: item.id,
        type: item.type,
        method: item.request.method,
        url: item.request.url,
        status: item.status ?? 'pending',
      }));

      return {
        status: 'success',
        result: {
          message:
            queue.length === 0
              ? 'Intercept queue is empty'
              : `Listed ${queue.length} intercepted item${queue.length === 1 ? '' : 's'}`,
          data: {
            interceptEnabled: currentSettings.interceptEnabled,
            interceptResponsesEnabled:
              currentSettings.interceptResponsesEnabled,
            queueLength: queue.length,
            queue,
          },
        },
      };
    }
    case 'forward':
    case 'drop':
    case 'forward-all':
    case 'drop-all': {
      const queueAction: InterceptQueueProcessAction =
        parsedInput.data.action === 'forward' ||
        parsedInput.data.action === 'forward-all'
          ? 'forward'
          : 'drop';

      if (
        parsedInput.data.action === 'forward' ||
        parsedInput.data.action === 'drop'
      ) {
        const queueItemId = parsedInput.data.queueItemId;
        if (!queueItemId) {
          return {
            status: 'error',
            result: {
              message: `queueItemId is required for ${parsedInput.data.action}`,
            },
          };
        }

        const item = useProxyStore
          .getState()
          .interceptQueue.find((queuedItem) => queuedItem.id === queueItemId);

        if (!item) {
          return {
            status: 'error',
            result: {
              message: `No queued item with queueItemId ${queueItemId}`,
            },
          };
        }

        const processResult = await processInterceptQueueItem({
          item,
          action: queueAction,
          proxyStore,
        });

        const currentSettings = useProxyStore.getState().settings;
        const actionLabel = queueAction === 'forward' ? 'Forwarded' : 'Dropped';
        const resultData = {
          interceptEnabled: currentSettings.interceptEnabled,
          interceptResponsesEnabled: currentSettings.interceptResponsesEnabled,
          queueLength: useProxyStore.getState().interceptQueue.length,
          processedCount: processResult.success ? 1 : 0,
        };

        if (!processResult.success) {
          console.warn(
            '[interceptToggleHandler] Failed to process queue item',
            {
              itemId: queueItemId,
              action: parsedInput.data.action,
              error: processResult.error ?? 'Unknown error',
            },
          );
          return {
            status: 'error',
            result: {
              message: `${actionLabel} failed for queueItemId ${queueItemId}`,
              data: resultData,
            },
          };
        }

        return {
          status: 'success',
          result: {
            message: `${actionLabel} queueItemId ${queueItemId}`,
            data: resultData,
          },
        };
      }

      const queue = [...useProxyStore.getState().interceptQueue];
      let processedCount = 0;
      const failedItemIds: string[] = [];

      for (const item of queue) {
        const processResult = await processInterceptQueueItem({
          item,
          action: queueAction,
          proxyStore,
        });

        if (processResult.success) {
          processedCount += 1;
        } else {
          failedItemIds.push(item.id);
          console.warn(
            '[interceptToggleHandler] Failed to process queue item',
            {
              itemId: item.id,
              action: parsedInput.data.action,
              error: processResult.error ?? 'Unknown error',
            },
          );
        }
      }

      const currentSettings = useProxyStore.getState().settings;
      const actionLabel = queueAction === 'forward' ? 'Forwarded' : 'Dropped';
      const failedCount = failedItemIds.length;
      const resultData = {
        interceptEnabled: currentSettings.interceptEnabled,
        interceptResponsesEnabled: currentSettings.interceptResponsesEnabled,
        queueLength: useProxyStore.getState().interceptQueue.length,
        processedCount,
      };

      if (failedCount > 0) {
        return {
          status: 'error',
          result: {
            message: `${actionLabel} ${processedCount} of ${queue.length} intercepted item(s); ${failedCount} failed`,
            data: resultData,
          },
        };
      }

      return {
        status: 'success',
        result: {
          message: `${actionLabel} ${processedCount} intercepted item(s)`,
          data: resultData,
        },
      };
    }
    default: {
      const interceptSettings = getInterceptSettingsForAction(
        parsedInput.data.action,
      );
      if (!interceptSettings) {
        return {
          status: 'error',
          result: {
            message: `Unknown action for ${INTERCEPT_TOGGLE_TOOL_NAME}`,
          },
        };
      }

      proxyStore.updateSettings(interceptSettings);

      const updatedSettings = useProxyStore.getState().settings;
      const queueLength = useProxyStore.getState().interceptQueue.length;

      return {
        status: 'success',
        result: {
          message: `Intercept mode updated (${parsedInput.data.action})`,
          data: {
            interceptEnabled: updatedSettings.interceptEnabled,
            interceptResponsesEnabled:
              updatedSettings.interceptResponsesEnabled,
            queueLength,
          },
        },
      };
    }
  }
};
